import { useState, useEffect } from "react";
import { useParams, Link } from "react-router";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { cn } from "@/lib/utils";
import { apiFetch } from "@/lib/api-client";
import { formatDate, formatTime } from "@/lib/i18n";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WebinarStats {
  webinarId: string;
  title: string;
  scheduledAt: string | null;
  status: string;
  registrationCount: number;
  attendeeCount: number;
  peakConcurrentViewers: number;
  avgWatchDuration: number;  // seconds
  avgEngagementScore: number;
}

interface Attendee {
  id: string;
  personId: string;
  name?: string;
  email?: string;
  joinedAt: string;
  leftAt: string | null;
  watchDurationSeconds: number;
  engagementScore: number | null;
}

interface PollResult {
  id: string;
  question: string;
  type: string;
  responses: number;
  options: Array<{ label: string; count: number; percentage: number }>;
}

interface TopQuestion {
  id: string;
  question: string;
  askedBy: string;
  upvotes: number;
  answered: boolean;
}

interface CtaPerformance {
  id: string;
  label: string;
  type: string;
  clickCount: number;
  impressions: number | null;
  ctr: number | null;
}

interface EngagementPoint {
  timestamp: string;
  attendeeCount: number;
  chatCount: number;
  pollVotes: number;
  reactions: number;
  dropOffPercent: number;
}

interface EngagementMarker {
  id: string;
  type: "poll" | "cta";
  label: string;
  timestamp: string | null;
}

interface EngagementSummary {
  peakAttendees: number;
  totalChatMessages: number;
  totalPollVotes: number;
  totalReactions: number;
  finalDropOffPercent: number;
}

interface EngagementResponse {
  webinarId: string;
  timeline: EngagementPoint[];
  markers: EngagementMarker[];
  summary: EngagementSummary;
}

type ViewMode = "post" | "live";

// ---------------------------------------------------------------------------
// Metric Card
// ---------------------------------------------------------------------------

function MetricCard({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div className="stagger-item rounded-lg border border-border bg-card p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-bold text-foreground">{value}</p>
      {detail && <p className="mt-0.5 text-xs text-muted-foreground">{detail}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Registration Funnel
// ---------------------------------------------------------------------------

function RegistrationFunnel({ registered, attended, completed }: {
  registered: number;
  attended: number;
  completed: number;
}) {
  const steps = [
    { label: "Registered", count: registered, color: "bg-[var(--color-primary)]" },
    { label: "Attended", count: attended, color: "bg-[var(--color-success)]/50" },
    { label: "Stayed to End", count: completed, color: "bg-[var(--color-secondary)]" },
  ];

  const maxCount = Math.max(registered, 1);

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <h3 className="mb-4 text-sm font-semibold text-foreground">Registration Funnel</h3>
      <div className="space-y-3">
        {steps.map((step) => {
          const pct = Math.round((step.count / maxCount) * 100);
          return (
            <div key={step.label}>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground">{step.label}</span>
                <span className="text-muted-foreground">{step.count} ({pct}%)</span>
              </div>
              <div className="mt-1 h-2 rounded-full bg-muted">
                <div className={cn("h-2 rounded-full transition-all", step.color)} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Engagement Distribution
// ---------------------------------------------------------------------------

function EngagementDistribution({ attendees }: { attendees: Attendee[] }) {
  const scored = attendees.filter((a) => a.engagementScore !== null);
  const buckets = [
    { label: "High (75-100)", min: 75, max: 100, color: "bg-[var(--color-success)]/50" },
    { label: "Medium (50-74)", min: 50, max: 74, color: "bg-[var(--color-warning)]/50" },
    { label: "Low (25-49)", min: 25, max: 49, color: "bg-[var(--color-accent-action)]" },
    { label: "Minimal (0-24)", min: 0, max: 24, color: "bg-[var(--color-destructive)]/50" },
  ];

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <h3 className="mb-4 text-sm font-semibold text-foreground">Engagement Distribution</h3>
      <div className="space-y-2">
        {buckets.map((bucket) => {
          const count = scored.filter(
            (a) => (a.engagementScore ?? 0) >= bucket.min && (a.engagementScore ?? 0) <= bucket.max,
          ).length;
          const pct = scored.length > 0 ? Math.round((count / scored.length) * 100) : 0;

          return (
            <div key={bucket.label} className="flex items-center gap-3">
              <div className={cn("h-3 w-3 shrink-0 rounded-full", bucket.color)} />
              <span className="w-32 text-xs text-foreground">{bucket.label}</span>
              <div className="flex-1 h-2 rounded-full bg-muted">
                <div className={cn("h-2 rounded-full", bucket.color)} style={{ width: `${pct}%` }} />
              </div>
              <span className="w-12 text-right text-xs text-muted-foreground">{count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Attendee Table
// ---------------------------------------------------------------------------

function AttendeeTable({ attendees }: { attendees: Attendee[] }) {
  const sorted = [...attendees].sort((a, b) => (b.engagementScore ?? 0) - (a.engagementScore ?? 0));

  function formatDuration(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  }

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold text-foreground">Attendees ({attendees.length})</h3>
        <button className="rounded-md border border-border px-3 py-1 text-xs text-muted-foreground hover:bg-accent">
          Export CSV
        </button>
      </div>
      <div className="max-h-64 overflow-y-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs text-muted-foreground">
              <th className="px-4 py-2">Contact</th>
              <th className="px-4 py-2">Joined</th>
              <th className="px-4 py-2">Watch Time</th>
              <th className="px-4 py-2">Engagement</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sorted.map((a) => (
              <tr key={a.id} className="hover:bg-accent/30">
                <td className="px-4 py-2 text-foreground">{a.name ?? a.personId.slice(0, 8)}</td>
                <td className="px-4 py-2 text-muted-foreground">
                  {formatTime(a.joinedAt)}
                </td>
                <td className="px-4 py-2 text-muted-foreground">{formatDuration(a.watchDurationSeconds)}</td>
                <td className="px-4 py-2">
                  {a.engagementScore !== null ? (
                    <span className={cn(
                      "rounded-full px-2 py-0.5 text-xs font-medium",
                      a.engagementScore >= 75 ? "bg-[var(--color-success)]/10 text-[var(--color-success)]" :
                      a.engagementScore >= 50 ? "bg-[var(--color-warning)]/10 text-[var(--color-warning)]" :
                      a.engagementScore >= 25 ? "bg-[var(--color-accent-action-light)] text-orange-700" :
                      "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
                    )}>
                      {a.engagementScore}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Poll Results
// ---------------------------------------------------------------------------

function PollResults({ polls }: { polls: PollResult[] }) {
  if (polls.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-2 text-sm font-semibold text-foreground">Poll Results</h3>
        <p className="text-xs text-muted-foreground">No polls were conducted.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <h3 className="mb-4 text-sm font-semibold text-foreground">Poll Results</h3>
      <div className="space-y-4">
        {polls.map((poll) => (
          <div key={poll.id}>
            <p className="text-sm font-medium text-foreground">{poll.question}</p>
            <p className="mb-2 text-xs text-muted-foreground">{poll.responses} responses</p>
            <div className="space-y-1">
              {poll.options.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="flex-1 h-5 rounded bg-muted">
                    <div
                      className="h-5 rounded bg-primary/20 px-2 text-xs leading-5 text-foreground"
                      style={{ width: `${Math.max(opt.percentage, 5)}%` }}
                    >
                      {opt.label}
                    </div>
                  </div>
                  <span className="w-12 text-right text-xs text-muted-foreground">{opt.percentage}%</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CTA Performance
// ---------------------------------------------------------------------------

function CtaPerformanceTable({ ctas }: { ctas: CtaPerformance[] }) {
  if (ctas.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-2 text-sm font-semibold text-foreground">CTA Performance</h3>
        <p className="text-xs text-muted-foreground">No CTAs were displayed.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <h3 className="mb-3 text-sm font-semibold text-foreground">CTA Performance</h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground">
            <th className="pb-2">CTA</th>
            <th className="pb-2">Type</th>
            <th className="pb-2">Clicks</th>
            <th className="pb-2">CTR</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {ctas.map((cta) => (
            <tr key={cta.id}>
              <td className="py-2 text-foreground">{cta.label}</td>
              <td className="py-2 text-muted-foreground">{cta.type}</td>
              <td className="py-2 text-foreground">{cta.clickCount}</td>
              <td className="py-2">
                <span className={cn(
                  "rounded-full px-2 py-0.5 text-xs font-medium",
                  (cta.ctr ?? 0) >= 10 ? "bg-[var(--color-success)]/10 text-[var(--color-success)]" :
                  (cta.ctr ?? 0) >= 5 ? "bg-[var(--color-warning)]/10 text-[var(--color-warning)]" :
                  "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
                )}>
                  {cta.ctr === null ? "—" : `${cta.ctr.toFixed(1)}%`}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top Questions
// ---------------------------------------------------------------------------

function TopQuestions({ questions }: { questions: TopQuestion[] }) {
  if (questions.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-2 text-sm font-semibold text-foreground">Top Questions</h3>
        <p className="text-xs text-muted-foreground">No questions were asked.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <h3 className="mb-3 text-sm font-semibold text-foreground">Top Questions</h3>
      <div className="space-y-2">
        {questions.slice(0, 10).map((q) => (
          <div key={q.id} className="flex items-start gap-3 text-sm">
            <span className="mt-0.5 rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
              {q.upvotes}
            </span>
            <div className="flex-1">
              <p className="text-foreground">{q.question}</p>
              <p className="text-xs text-muted-foreground">by {q.askedBy}</p>
            </div>
            {q.answered && (
              <span className="rounded-full bg-[var(--color-success)]/10 px-2 py-0.5 text-xs text-[var(--color-success)]">Answered</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function EngagementCurve({ engagement }: { engagement: EngagementResponse | null }) {
  if (!engagement || engagement.timeline.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-2 text-sm font-semibold text-foreground">Engagement Curve</h3>
        <p className="text-xs text-muted-foreground">No minute-level engagement data is available for this webinar yet.</p>
      </div>
    );
  }

  const maxAttendees = Math.max(...engagement.timeline.map((point) => point.attendeeCount), 1);
  const maxChat = Math.max(...engagement.timeline.map((point) => point.chatCount), 1);

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Engagement Curve</h3>
          <p className="text-xs text-muted-foreground">Minute buckets for audience presence, chat, poll votes, and reactions.</p>
        </div>
        <div className="text-right text-xs text-muted-foreground">
          <div>Peak attendees: {engagement.summary.peakAttendees}</div>
          <div>Final drop-off: {engagement.summary.finalDropOffPercent}%</div>
        </div>
      </div>

      <div className="space-y-3">
        {engagement.timeline.map((point) => (
          <div key={point.timestamp} className="grid gap-2 rounded-xl border border-border/60 bg-background px-3 py-3 md:grid-cols-[92px_minmax(0,1fr)_72px]">
            <div className="text-xs text-muted-foreground">
              {formatTime(point.timestamp)}
            </div>
            <div className="space-y-2">
              <div>
                <div className="mb-1 flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>Attendees</span>
                  <span>{point.attendeeCount}</span>
                </div>
                <div className="h-2 rounded-full bg-muted">
                  <div className="h-2 rounded-full bg-primary" style={{ width: `${Math.max((point.attendeeCount / maxAttendees) * 100, 4)}%` }} />
                </div>
              </div>
              <div>
                <div className="mb-1 flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>Chat</span>
                  <span>{point.chatCount} · Polls {point.pollVotes} · Reactions {point.reactions}</span>
                </div>
                <div className="h-2 rounded-full bg-muted">
                  <div className="h-2 rounded-full bg-secondary" style={{ width: `${Math.max((point.chatCount / maxChat) * 100, point.chatCount > 0 ? 6 : 0)}%` }} />
                </div>
              </div>
            </div>
            <div className="text-right text-xs text-muted-foreground">
              <div>Drop-off</div>
              <div className="font-medium text-foreground">{point.dropOffPercent}%</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EngagementMarkers({ engagement }: { engagement: EngagementResponse | null }) {
  if (!engagement || engagement.markers.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-2 text-sm font-semibold text-foreground">Engagement Markers</h3>
        <p className="text-xs text-muted-foreground">No poll or CTA markers were captured for this webinar.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <h3 className="mb-3 text-sm font-semibold text-foreground">Engagement Markers</h3>
      <div className="space-y-2">
        {engagement.markers.map((marker) => (
          <div key={marker.id} className="flex items-center justify-between rounded-lg border border-border/60 bg-background px-3 py-2 text-sm">
            <div>
              <div className="font-medium text-foreground">{marker.label}</div>
              <div className="text-xs text-muted-foreground">{marker.type === "cta" ? "CTA marker" : "Poll marker"}</div>
            </div>
            <div className="text-xs text-muted-foreground">
              {marker.timestamp ? formatTime(marker.timestamp) : "Runtime marker"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function WebinarAnalyticsPage() {
  const { webinarId } = useParams();
  const webinarBasePath = webinarId ? `/v1/webinars/${webinarId}` : null;
  const [stats, setStats] = useState<WebinarStats | null>(null);
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [polls, setPolls] = useState<PollResult[]>([]);
  const [questions, setQuestions] = useState<TopQuestion[]>([]);
  const [ctas, setCtas] = useState<CtaPerformance[]>([]);
  const [engagement, setEngagement] = useState<EngagementResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("post");

  useEffect(() => {
    if (!webinarBasePath) return;
    setIsLoading(true);

    Promise.all([
      apiFetch<{ data: WebinarStats }>(`${webinarBasePath}/analytics/summary`)
        .then((r) => setStats(r.data))
        .catch(() => setStats(null)),
      apiFetch<{ data: Attendee[] }>(`${webinarBasePath}/attendance`)
        .then((r) => setAttendees(r.data ?? []))
        .catch(() => setAttendees([])),
      apiFetch<{ data: PollResult[] }>(`${webinarBasePath}/analytics/polls`)
        .then((r) => setPolls(r.data ?? []))
        .catch(() => setPolls([])),
      apiFetch<{ data: Array<{ id: string; contactId: string; question: string; answer: string | null; upvotes: number }> }>(`${webinarBasePath}/questions`)
        .then((r) => setQuestions((r.data ?? []).map((question) => ({
          id: question.id,
          question: question.question,
          askedBy: question.contactId.slice(0, 8),
          upvotes: question.upvotes,
          answered: Boolean(question.answer),
        }))))
        .catch(() => setQuestions([])),
      apiFetch<{ data: Array<{ id: string; label: string; type: string; clickCount: number; impressions: number | null; ctr: number | null }> }>(`${webinarBasePath}/analytics/cta-performance`)
        .then((r) => setCtas(r.data ?? []))
        .catch(() => setCtas([])),
      apiFetch<{ data: EngagementResponse }>(`${webinarBasePath}/analytics/engagement`)
        .then((r) => setEngagement(r.data))
        .catch(() => setEngagement(null)),
    ])
      .catch(() => setError("Failed to load analytics"))
      .finally(() => setIsLoading(false));
  }, [webinarBasePath]);

  if (!webinarId) return null;

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-64 rounded" />
        <div className="grid gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonCard key={i} className="h-24 rounded-lg" />
          ))}
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-48 rounded-lg" />
          <Skeleton className="h-48 rounded-lg" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-destructive">{error}</p>
          <button onClick={() => window.location.reload()} className="mt-2 text-xs text-primary hover:underline">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const registered = stats?.registrationCount ?? 0;
  const attended = stats?.attendeeCount ?? attendees.length;
  const completedCount = attendees.filter((a) => a.leftAt === null || a.watchDurationSeconds > 0).length;

  function formatDuration(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  }

  async function handleExportEngagementCsv() {
    if (!webinarBasePath) return;

    const response = await fetch(`${webinarBasePath}/analytics/engagement/export`, {
      credentials: "include",
    });
    if (!response.ok) {
      setError("Failed to export engagement CSV");
      return;
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `webinar-engagement-${webinarId}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link to="/webinars" className="text-xs text-muted-foreground hover:text-foreground">
            ← Back to Webinars
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-foreground">
            {stats?.title ?? "Webinar Analytics"}
          </h1>
          {stats?.scheduledAt && (
            <p className="text-sm text-muted-foreground">
              {formatDate(stats.scheduledAt)} at {formatTime(stats.scheduledAt)}
            </p>
          )}
        </div>
        <div className="flex gap-1 rounded-lg border border-border p-1">
          <button
            onClick={() => setViewMode("post")}
            className={cn(
              "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              viewMode === "post" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent",
            )}
          >
            Post-Webinar
          </button>
          <button
            onClick={() => setViewMode("live")}
            className={cn(
              "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              viewMode === "live" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent",
            )}
          >
            Live View
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <MetricCard label="Registered" value={String(registered)} />
        <MetricCard label="Attended" value={String(attended)} detail={registered > 0 ? `${Math.round((attended / registered) * 100)}% show rate` : undefined} />
        <MetricCard label="Avg Watch Time" value={formatDuration(stats?.avgWatchDuration ?? 0)} />
        <MetricCard label="Avg Engagement" value={String(stats?.avgEngagementScore ?? 0)} detail="out of 100" />
      </div>

      {viewMode === "post" ? (
        <>
          {/* Funnel + Engagement Distribution */}
          <div className="grid gap-4 md:grid-cols-2">
            <RegistrationFunnel registered={registered} attended={attended} completed={completedCount} />
            <EngagementDistribution attendees={attendees} />
          </div>

          {/* Polls + CTA + Questions */}
          <div className="grid gap-4 md:grid-cols-2">
            <PollResults polls={polls} />
            <CtaPerformanceTable ctas={ctas} />
          </div>

          <TopQuestions questions={questions} />

          {/* Attendee Table */}
          <AttendeeTable attendees={attendees} />
        </>
      ) : (
        /* Live View */
        <div className="space-y-4">
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={() => void handleExportEngagementCsv()}
              className="rounded-md border border-border px-3 py-2 text-xs text-muted-foreground hover:bg-accent"
            >
              Export CSV
            </button>
          </div>
          <div className="grid gap-4 md:grid-cols-4">
            <MetricCard label="Peak Attendees" value={String(engagement?.summary.peakAttendees ?? 0)} />
            <MetricCard label="Chat Messages" value={String(engagement?.summary.totalChatMessages ?? 0)} />
            <MetricCard label="Poll Votes" value={String(engagement?.summary.totalPollVotes ?? 0)} />
            <MetricCard label="Reactions" value={String(engagement?.summary.totalReactions ?? 0)} detail={`${engagement?.summary.finalDropOffPercent ?? 0}% final drop-off`} />
          </div>
          <div className="grid gap-4 md:grid-cols-[minmax(0,1.4fr)_minmax(320px,1fr)]">
            <EngagementCurve engagement={engagement} />
            <EngagementMarkers engagement={engagement} />
          </div>
        </div>
      )}
    </div>
  );
}
